import { useEffect, useState } from 'react';
import { Loader2, PhoneCall } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import {
  BARREIRAS_BUSCA_ATIVA,
  STATUS_LABELS,
  buscaAtivaService,
  type BuscaAtivaStatus,
  type CasoBuscaAtiva,
} from '@/domains';
import { toast } from '@/hooks/use-toast';

interface RegistrarContatoModalProps {
  alunoId: string;
  nomeAluno: string;
  escolaId: string;
  caso?: CasoBuscaAtiva;
  onSuccess: (novoRegistro?: Record<string, unknown>, casoAtualizado?: CasoBuscaAtiva) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
  defaultStatusFunil?: BuscaAtivaStatus;
}

const STATUS_OPTIONS = Object.entries(STATUS_LABELS) as Array<[BuscaAtivaStatus, string]>;

export function RegistrarContatoModal({
  alunoId,
  nomeAluno,
  escolaId,
  caso,
  onSuccess,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  hideTrigger,
  defaultStatusFunil,
}: RegistrarContatoModalProps) {
  const { user } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;

  const [loading, setLoading] = useState(false);
  const [formaContato, setFormaContato] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [statusFunil, setStatusFunil] = useState<BuscaAtivaStatus>(
    defaultStatusFunil || caso?.status || 'CONTATO_PENDENTE',
  );
  const [barreira, setBarreira] = useState(caso?.barreira_principal || '');
  const [responsavelNome, setResponsavelNome] = useState(
    caso?.responsavel_nome || user?.username || user?.email || '',
  );
  const [proximaAcao, setProximaAcao] = useState(caso?.proxima_acao || '');
  const [followUp, setFollowUp] = useState(
    caso?.proximo_follow_up ? caso.proximo_follow_up.slice(0, 16) : '',
  );
  const [dataRetorno, setDataRetorno] = useState(
    caso?.retornou_em || caso?.data_prevista_retorno || '',
  );
  const [motivoFechamento, setMotivoFechamento] = useState(caso?.motivo_fechamento || '');

  useEffect(() => {
    if (!open) return;
    setStatusFunil(defaultStatusFunil || caso?.status || 'CONTATO_PENDENTE');
    setBarreira(caso?.barreira_principal || '');
    setResponsavelNome(caso?.responsavel_nome || user?.username || user?.email || '');
    setProximaAcao(caso?.proxima_acao || '');
    setFollowUp(caso?.proximo_follow_up ? caso.proximo_follow_up.slice(0, 16) : '');
    setDataRetorno(caso?.retornou_em || caso?.data_prevista_retorno || '');
    setMotivoFechamento(caso?.motivo_fechamento || '');
  }, [caso, defaultStatusFunil, open, user?.email, user?.username]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formaContato) {
      toast({ title: 'Selecione a forma de contato', variant: 'destructive' });
      return;
    }
    if (!justificativa.trim()) {
      toast({
        title: 'Registre o resultado ou a atualização do caso',
        variant: 'destructive',
      });
      return;
    }
    if (
      ['RETORNO_COMBINADO', 'EM_OBSERVACAO', 'RECUPERADO'].includes(statusFunil) &&
      !dataRetorno
    ) {
      toast({
        title:
          statusFunil === 'RETORNO_COMBINADO'
            ? 'Informe a data prevista de retorno'
            : 'Confirme a data em que o aluno retornou',
        variant: 'destructive',
      });
      return;
    }
    if (statusFunil === 'RECUPERADO' && caso?.status !== 'EM_OBSERVACAO') {
      toast({
        title: 'O aluno precisa passar pela etapa Em observação',
        description: 'Confirme primeiro o retorno e acompanhe a estabilidade da frequência.',
        variant: 'destructive',
      });
      return;
    }
    if (statusFunil === 'EVASAO_CONFIRMADA' && !motivoFechamento.trim()) {
      toast({
        title: 'Informe o motivo da evasão confirmada',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const now = new Date().toISOString();
      const novoRegistro = {
        aluno_id: alunoId,
        escola_id: escolaId,
        forma_contato: formaContato,
        justificativa_faltas: justificativa.trim(),
        status_funil: statusFunil,
        data_contato: now,
        created_at: now,
        monitor_responsavel: responsavelNome || user?.username || user?.email || 'Equipe escolar',
        resultado_contato:
          statusFunil === 'AGUARDANDO_FAMILIA' ? 'SEM_RESPOSTA' : 'ATUALIZACAO_REGISTRADA',
      };

      const firstContact =
        caso?.primeiro_contato_em ||
        (formaContato !== 'Atualização interna' ? now : null);

      const casoPayload: CasoBuscaAtiva = {
        escola_id: escolaId,
        aluno_id: alunoId,
        status: statusFunil,
        nivel_risco:
          statusFunil === 'EM_OBSERVACAO'
            ? 'OBSERVACAO'
            : caso?.nivel_risco || 'MEDIO',
        score_risco:
          statusFunil === 'EM_OBSERVACAO'
            ? Math.min(caso?.score_risco || 0, 30)
            : caso?.score_risco || 0,
        motivos_risco: caso?.motivos_risco || [],
        barreira_principal: barreira || null,
        responsavel_user_id: user?.id || null,
        responsavel_nome: responsavelNome || null,
        proxima_acao: proximaAcao || null,
        proximo_follow_up: followUp ? new Date(followUp).toISOString() : null,
        primeiro_contato_em: firstContact,
        data_prevista_retorno:
          statusFunil === 'RETORNO_COMBINADO' && dataRetorno ? dataRetorno : caso?.data_prevista_retorno,
        retornou_em:
          ['EM_OBSERVACAO', 'RECUPERADO'].includes(statusFunil) && dataRetorno
            ? dataRetorno
            : caso?.retornou_em,
        recuperado_em: statusFunil === 'RECUPERADO' ? now : null,
        fechado_em: ['RECUPERADO', 'EVASAO_CONFIRMADA'].includes(statusFunil) ? now : null,
        motivo_fechamento: statusFunil === 'EVASAO_CONFIRMADA' ? motivoFechamento.trim() : null,
      };

      const casoAtualizado = await buscaAtivaService.registrarAcao({
        escolaId,
        alunoId,
        formaContato,
        anotacao: justificativa.trim(),
        status: statusFunil,
        monitorResponsavel: novoRegistro.monitor_responsavel,
        caso: casoPayload,
      });

      toast({
        title: statusFunil === 'RECUPERADO' ? 'Recuperação registrada' : 'Caso atualizado',
        description: 'O histórico, o responsável e a próxima ação foram salvos.',
      });
      setOpen(false);
      setFormaContato('');
      setJustificativa('');
      setProximaAcao('');
      onSuccess(novoRegistro, casoAtualizado);
    } catch (error: any) {
      console.error('Erro ao salvar Busca Ativa:', error);
      toast({
        title: 'Não foi possível salvar',
        description:
          error?.message ||
          'Verifique se a migration do CRM de Busca Ativa já foi aplicada.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const needsReturnDate = ['RETORNO_COMBINADO', 'EM_OBSERVACAO', 'RECUPERADO'].includes(
    statusFunil,
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button size="sm" className="min-h-9 bg-indigo-600 hover:bg-indigo-700">
            <PhoneCall className="mr-2 h-4 w-4" />
            Registrar ação
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[620px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Atualizar caso de Busca Ativa</DialogTitle>
            <DialogDescription>
              {nomeAluno} · toda mudança fica registrada no histórico.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-5 py-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-2 text-sm font-medium">
                <span>Forma de contato ou ação</span>
                <Select value={formaContato} onValueChange={setFormaContato}>
                  <SelectTrigger className="min-h-10">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                    <SelectItem value="Ligação Telefônica">Ligação telefônica</SelectItem>
                    <SelectItem value="Visita Domiciliar">Visita domiciliar</SelectItem>
                    <SelectItem value="Reunião na Escola">Reunião na escola</SelectItem>
                    <SelectItem value="Encaminhamento">Encaminhamento</SelectItem>
                    <SelectItem value="Atualização interna">Atualização interna</SelectItem>
                  </SelectContent>
                </Select>
              </label>

              <label className="space-y-2 text-sm font-medium">
                <span>Etapa do caso</span>
                <Select value={statusFunil} onValueChange={value => setStatusFunil(value as BuscaAtivaStatus)}>
                  <SelectTrigger className="min-h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            </div>

            <label className="space-y-2 text-sm font-medium">
              <span>Resultado, contexto ou nota</span>
              <Textarea
                placeholder="O que foi conversado? Qual dificuldade apareceu? Evite registrar detalhes sensíveis desnecessários."
                value={justificativa}
                onChange={event => setJustificativa(event.target.value)}
                rows={3}
              />
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-2 text-sm font-medium">
                <span>Barreira principal</span>
                <Select value={barreira} onValueChange={setBarreira}>
                  <SelectTrigger className="min-h-10">
                    <SelectValue placeholder="Ainda não identificada" />
                  </SelectTrigger>
                  <SelectContent>
                    {BARREIRAS_BUSCA_ATIVA.map(item => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>

              <label className="space-y-2 text-sm font-medium">
                <span>Responsável pelo caso</span>
                <Input
                  value={responsavelNome}
                  onChange={event => setResponsavelNome(event.target.value)}
                  placeholder="Nome do profissional ou setor"
                />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-2 text-sm font-medium">
                <span>Próxima ação</span>
                <Input
                  value={proximaAcao}
                  onChange={event => setProximaAcao(event.target.value)}
                  placeholder="Ex.: ligar novamente ou acionar assistência"
                />
              </label>

              <label className="space-y-2 text-sm font-medium">
                <span>Data do follow-up</span>
                <Input
                  type="datetime-local"
                  value={followUp}
                  onChange={event => setFollowUp(event.target.value)}
                />
              </label>
            </div>

            {needsReturnDate && (
              <label className="space-y-2 text-sm font-medium">
                <span>
                  {statusFunil === 'RETORNO_COMBINADO'
                    ? 'Data prevista de retorno'
                    : 'Data confirmada de retorno'}
                </span>
                <Input
                  type="date"
                  value={dataRetorno}
                  onChange={event => setDataRetorno(event.target.value)}
                />
              </label>
            )}

            {statusFunil === 'EVASAO_CONFIRMADA' && (
              <label className="space-y-2 text-sm font-medium">
                <span>Motivo formal do encerramento</span>
                <Textarea
                  value={motivoFechamento}
                  onChange={event => setMotivoFechamento(event.target.value)}
                  placeholder="Informe a situação confirmada, sem expor dados sensíveis."
                  rows={2}
                />
              </label>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar ação e próxima etapa
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
